import {
  Component,
  Input,
  Output,
  EventEmitter,
  inject,
  type OnChanges,
  type SimpleChanges,
} from '@angular/core';
import type { Offer } from '@innogy/become-a-customer/shared/interfaces';
import {
  FilterEntry,
  FilterComponent,
  RichTextWrapperComponent,
} from '@legacy/common-ui-utility-components';
import { ConsumptionValuesVM } from '@innogy/become-a-customer/shared/interfaces';
import type { StreetAddress } from '@essent/new-customer';
import { ENVIRONMENT_CONFIG } from '@core/config-angular';
import { TranslatePipe } from '@ngx-translate/core';
import { OfferSaverButtonComponent } from '@innogy/offer-saver';

import { CommodityOfferContent } from '../commodity-offers.model';
import { getOfferOptions, groupOffersByContractType } from '../utils';
import { CommodityOfferHeaderComponent } from '../commodity-offer-header/commodity-offer-header.component';
import { EdConsumerAcquisitionCommodityOfferComponent } from '../commodity-offer-brand/ed-consumer-acquisition-commodity-offer/ed-consumer-acquisition-commodity-offer.component';
import { CommodityOfferCalculationBasedOnComponent } from '../commodity-offer-calculation-based-on/commodity-offer-calculation-based-on.component';
import { EssentConsumerAcquisitionCommodityOfferComponent } from '../commodity-offer-brand/essent-consumer-acquisition-commodity-offer/essent-consumer-acquisition-commodity-offer.component';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'wl-mixed-commodity-offer',
  templateUrl: './mixed-commodity-offer.component.html',
  styleUrls: [
    './mixed-commodity-offer.component.ed.scss',
    './mixed-commodity-offer.component.essent.scss',
  ],
  standalone: true,
  imports: [
    TranslatePipe,
    CommodityOfferHeaderComponent,
    EdConsumerAcquisitionCommodityOfferComponent,
    OfferSaverButtonComponent,
    EssentConsumerAcquisitionCommodityOfferComponent,
    CommodityOfferCalculationBasedOnComponent,
    CommonModule,
    FilterComponent,
    RichTextWrapperComponent,
  ],
})
export class MixedCommodityOfferComponent implements OnChanges {
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() content?: CommodityOfferContent;
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() showMemberGetMember = false;
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() consumptionValues?: ConsumptionValuesVM;
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() supplyAddress?: Partial<StreetAddress>;
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() offers: Offer[] | undefined;
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() hasSolarPanels = false;

  @Output() selectOffer = new EventEmitter<Offer>();
  @Output() openMoreInfo = new EventEmitter<Offer>();
  @Output() viewTariffs = new EventEmitter<Offer>();
  @Output() adjustCalculation = new EventEmitter<void>();

  brand = inject(ENVIRONMENT_CONFIG).brand;
  offerContent?: CommodityOfferContent;
  shownOffer?: Offer;
  offerOptions?: FilterEntry<Offer>[];
  contractTypes?: FilterEntry<Offer[]>[];
  filteredOffers?: Offer[];

  initiallySelectedOfferOption?: FilterEntry<Offer>;
  initiallySelectedContract?: FilterEntry<Offer[]>;

  ngOnChanges(changes: SimpleChanges): void {
    const content = changes?.['content']?.currentValue;
    const offers = changes?.['offers']?.currentValue;

    if (content) {
      this.offerContent = content;
    }

    if (offers) {
      this.offerContent?.enableFilterByContractType
        ? this.enableFilterByContract(offers)
        : this.showOffersOptions(offers);
    }
  }

  showOffer(offer: Offer): void {
    this.shownOffer = offer;
  }

  showOffersOptions(offersFromContract: Offer[]): void {
    this.filteredOffers = offersFromContract;
    const offerOptions = getOfferOptions(offersFromContract);
    const firstOfferOption = offerOptions[0];

    this.offerOptions = offerOptions
      .slice()
      .sort((optionA, optionB) => optionA.text.localeCompare(optionB.text));
    this.initiallySelectedOfferOption = firstOfferOption;
    this.showOffer(firstOfferOption?.value);
  }

  private readonly enableFilterByContract = (offers: Offer[]) => {
    // Group offers by contract type
    this.contractTypes = groupOffersByContractType(offers, this.offerContent);

    // Set the first contract type
    const firstContractType = this.contractTypes[0];
    this.initiallySelectedContract = firstContractType;

    // Show the first contract's offers
    if (firstContractType?.value) {
      this.showOffersOptions(firstContractType.value);
    }
  };
}
